import api from 'api/instance';
import { FetchQueryType, FetchResponse } from 'types/common';
import { Status } from 'types/items';
import { CollectionPoint } from 'types/location';
import { Pickup } from 'types/pickup';
import { toMidnightISOString } from 'utils/dateUtils';

const URL = `${process.env.REACT_APP_API_URL}`;

type PickupPayload = {
  date: Date;
  collectionPoint: CollectionPoint;
};

export const createPickup = async (payload: PickupPayload) => {
  const { data } = await api.post(`${URL}/pickup/create`, payload);

  return data;
};

export type BulkCreatePickupItem = {
  date: string;
  eventName?: string;
  notes?: string;
  isFinalPickup: boolean;
};

export type BulkCreatePickupPayload = {
  collectionPoint: string;
  items: BulkCreatePickupItem[];
};

export const bulkCreatePickup = async (payload: BulkCreatePickupPayload) => {
  const { data } = await api.post(`${URL}/pickup/bulk-create`, payload);

  return data;
};

export const fetchPickups = async (
  params: FetchQueryType & {
    //status?: Status;
    startDate?: Date;
    endDate?: Date;
    venue?: string[];
    collectionPoint?: string[];
    sortByField?: string;
    sort?: number;
  },
) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.get<FetchResponse<Pickup>>(
    `${URL}/pickup/find-all`,
    {
      params: {
        ...params,
        startDate,
        endDate,
      },
    },
  );

  return data;
};

export const fetchPickupById = async ({ id }: { id: string }) => {
  const { data } = await api.get(`${URL}/pickup/find-one/${id}`);

  return data.item as Pickup;
};

export const deletePickups = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/pickup/remove-many`, {
    data: { ids },
  });

  return data;
};

export const updatePickup = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/pickup/update/${_id}`, _payload);

  return data;
};

export const getPickupCounts = async (
  washHub: string,
  initialStartDate?: Date,
  initialEndDate?: Date,
  venue?: string[],
) => {
  const endDate = toMidnightISOString(initialEndDate);
  const startDate = toMidnightISOString(initialStartDate);

  const { data } = await api.get(`${URL}/pickup/get-counts`, {
    params: {
      washHub,
      endDate,
      startDate,
      venue,
    },
  });

  return data?.item;
};
export const exportPickupsCsv = async (payload: {
  washHub: string;
  status?: number;
  startDate?: Date;
  endDate?: Date;
  venue?: string[];
}) => {
  const endDate = toMidnightISOString(payload.endDate);
  const startDate = toMidnightISOString(payload.startDate);

  const { data } = await api.post<any>(`${URL}/pickup/export-pickups`, {
    ...payload,
    startDate,
    endDate,
  });
  return data;
};

export const getPickupImages = async (pickupId: string) => {
  const { data } = await api.get(`${URL}/pickup/get-image-urls/${pickupId}`);

  return data?.item as {
    signature?: string;
    proofOfHarvestFiles?: Array<string>;
    proofOfPickupFiles?: Array<string>;
  };
};

export type HarvestCountPayload = {
  sku: string;
  dirtyQuantity: number;
  unusedQuantity: number;
};

/**
 * Persists admin-edited per-item-sku harvest counts. Dirty counts rewrite
 * pickedUpStats; unused counts are stored as item-level overrides
 * (unusedItemStats) on the pickup.
 */
export const editHarvestCounts = async (payload: {
  pickupId: string;
  counts: HarvestCountPayload[];
}) => {
  const { pickupId, counts } = payload;

  const { data } = await api.patch(
    `${URL}/pickup/edit-harvest-counts/${pickupId}`,
    {
      counts,
    },
  );

  return data;
};
